import { Component, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { DynamicFilterConfig, createDynamicFilterState } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { PaginatorState } from 'primeng/paginator';
import { SortEvent } from 'primeng/api';
import _ from 'lodash';
import { EmployeeAdvance, EmployeeAdvanceSearchCommand, EmployeeAdvanceStatus } from '../../../models/employee-advance/employee-advance';
import { PettyCashRoles } from '../../../models/roles/roles-group';
import { EmployeeAdvanceProxyService } from '../../../proxies/employee-advance.proxy.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { Currency, currencySymbol } from '../../../utilities/currency.util';
import { CASH_DOCUMENT_STATUSES, cashDocumentStatusLabel, cashDocumentStatusSeverity } from '../cash-document-status.util';

/** The filter panel's controls; select values arrive as strings. */
interface EmployeeAdvanceFilterFormModel {
  search: string | null;
  status: string | null;
}

type EmployeeAdvanceFilters = Partial<EmployeeAdvanceFilterFormModel>;

/** Employee advances (Anticipo de viaje, #5789): cash Treasury hands employees for their trips. */
@Component({
  selector: 'app-employee-advances',
  templateUrl: './employee-advances.component.html',
})
export class EmployeeAdvancesComponent {
  private readonly proxy = inject(EmployeeAdvanceProxyService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly userAuth = inject(UserAuthenticationService);

  private currentSearchCriteria: EmployeeAdvanceSearchCommand = {};

  readonly canPay = this.userAuth.roles.includes(PettyCashRoles.PettyCashTreasurer);

  readonly isLoading = signal(0);
  readonly showFilters = signal(true);
  readonly filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'employee-advance-filters', config: this.filterConfig });
  readonly items = signal<EmployeeAdvance[]>([]);
  readonly totalRecords = signal(0);
  readonly currentPage = signal(1);
  readonly pageSize = signal(25);
  readonly sortField = signal<string | null>(null);
  readonly sortOrder = signal(-1);

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters as EmployeeAdvanceFilters));

    effect(
      () => {
        if (this.translateService.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private t(key: string, fallback: string): string {
    const value = this.translateService.instant(key);
    return value === key ? fallback : value;
  }

  private tryInitializeFilterConfig(): void {
    this.filterConfig.set(this.buildFilterConfig());
  }

  private buildFilterConfig(): DynamicFilterConfig {
    return {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'search',
          label: this.t('EmployeeAdvance_Filter_Search', 'Number or employee'),
          placeholder: this.t('EmployeeAdvance_Filter_Search_Placeholder', 'Search by number or employee'),
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'status',
          label: this.t('Status', 'Status'),
          placeholder: this.t('Select_status', 'Select status'),
          options: CASH_DOCUMENT_STATUSES.map((status) => ({ label: this.statusLabel(status), value: String(status) })),
          showClear: true,
          defaultVisible: true,
        },
      ],
    };
  }

  private searchWithFilters(filters: EmployeeAdvanceFilters): void {
    const search = (filters.search ?? '').toString().trim();
    const status = _.isNil(filters.status) || filters.status === '' ? null : (Number(filters.status) as EmployeeAdvanceStatus);
    this.currentSearchCriteria = { search: search || null, statuses: status === null ? null : [status] };
    this.currentPage.set(1);
    this.search();
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.search();
  }

  onSort(event: SortEvent): void {
    const newField = event.field ?? null;
    const newOrder = event.order ?? 1;
    if (newField === this.sortField() && newOrder === this.sortOrder()) return;
    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.search();
  }

  private search(): void {
    this.startLoading();
    this.proxy
      .search(this.currentSearchCriteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        this.items.set(response.data?.items ?? []);
        this.totalRecords.set(response.data?.totalCount ?? 0);
      });
  }

  redirectToDetails(id: string): void {
    this.router.navigate(['/employee-advance-details'], { queryParams: { id } });
  }

  createAdvance(): void {
    this.redirectToDetails('new');
  }

  statusLabel(status: EmployeeAdvanceStatus | number): string {
    return cashDocumentStatusLabel(status, (k, f) => this.t(k, f));
  }

  statusSeverity(status: EmployeeAdvanceStatus): 'success' | 'warn' | 'secondary' {
    return cashDocumentStatusSeverity(status);
  }

  symbolOf(currency: Currency): string {
    return currencySymbol(currency);
  }

  tripsOf(advance: EmployeeAdvance): string {
    return advance.lines.map((l) => l.tripName).join(', ');
  }

  getRow(rowData: unknown): EmployeeAdvance {
    return rowData as EmployeeAdvance;
  }
}
